import { toast } from "react-toastify";
import NO_IMAGE_PLACEHOLDER from "../assets/default_placeholder.png";

export function formatPrice(amount, currency) {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    throw new TypeError("The amount must be a valid number.");
  }

  try {
    new Intl.NumberFormat("en-US", { style: "currency", currency });
  } catch {
    throw new TypeError("The currency must be a valid ISO 4217 currency code.");
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  })
    .format(amount)
    ?.replace(/^(\D+)(\d)/, "$1 $2");
}

/**
 * Returns the display string for a product's price or sale-type flag
 * (by auction, upon request, not for sale). Reusable across Cart, ProductDetails, and ProductCard.
 *
 * @param {number|null|undefined} price - Product unit price (-1 means no fixed price / upon request or auction)
 * @param {string} currency - ISO 4217 currency code (e.g. "EUR")
 * @param {{ forSale?: boolean, authorUsername?: string }} [options] - Optional: forSale and authorUsername to resolve flag when price < 0
 * @param {function(string): string} t - Translation function (e.g. from TranslationContext)
 * @returns {string} Formatted price or translated flag label
 */
export function getProductPriceDisplayValue(price, currency, options, t) {
  if (price == null || typeof price !== "number" || Number.isNaN(price)) {
    return "N/A";
  }
  if (price >= 0) {
    return currency ? formatPrice(price, currency) : "N/A";
  }
  const { forSale, authorUsername } = options || {};
  if (price === -1 && authorUsername === "surplex") {
    return t("byAuction");
  }
  if (forSale === true) return t("uponRequest");
  if (forSale === false) return t("notForSale");
  return t("uponRequest");
}

export function handleNonValidImg(event) {
  event.target.onerror = null;
  event.target.src = NO_IMAGE_PLACEHOLDER;
}

/**
 * Extracts the imageLink from a product's defaultImage object.
 * Returns the imageLink as-is without any URL prefix.
 *
 * @param {Object} product - Product object with optional defaultImage property
 * @returns {string} The imageLink or empty string if not available
 */
export function getDefaultImageUrl(product) {
  return product?.defaultImage?.imageLink || "";
}

export function scrollToTop() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
}

export function extractInitialValuesFromYupSchema(schema) {
  const initialValues = {};

  Object.keys(schema.fields).forEach((key) => {
    const field = schema.fields[key];

    // Check if the field has a default value
    const defaultValue = field.default ? field.getDefault() : undefined;

    // Handle object types recursively
    if (field.type === "object") {
      initialValues[key] = extractInitialValuesFromYupSchema(field);
    }
    // Handle array types
    else if (field.type === "array") {
      initialValues[key] = defaultValue || [];
    }
    // Handle all other types
    else {
      initialValues[key] = defaultValue;
    }
  });

  return initialValues;
}

function isJsonString(str) {
  if (typeof str !== "string") return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

export function parseData(input, errorHandler = console.error) {
  if (typeof input !== "string") {
    errorHandler("Invalid input: Expected a string");
    return {};
  }
  if (input.trim() === "") {
    errorHandler("Invalid input: Expected a non-empty string");
    return {};
  }

  if (!isJsonString(input)) {
    errorHandler("Invalid input: Expected a valid JSON string");
    return {};
  }

  try {
    return JSON.parse(input);
  } catch (e) {
    errorHandler(e);
    return {};
  }
}

export function getEnvType() {
  // eslint-disable-next-line no-undef
  const runType = process.env.NODE_ENV || "development";
  if (!["development", "production", "test"].includes(runType)) {
    console.warn(`Unexpected NODE_ENV value: ${runType}`);
  }
  return runType === "development" ? "dev" : "prod";
}

/**
 * Format a string to be more human-readable by adding spaces
 * between words and capitalizing the first letter of each word.
 * This function is useful for formatting labels. For example,\
 * "productRequestStatus" becomes "Product Request Status".
 * This function does not handle acronyms or initialisms.
 * For example, "apiURL" becomes "Api U R L".
 * This function does not handle acronyms or initialisms.
 *
 * @param {string} inputString
 * @returns {string}
 */
export function formatLabel(inputString) {
  // Split the string on camelCase, underscores, and hyphens
  const words = inputString
    .split(/(?=[A-Z])|[_-]/)
    .filter((word) => word.trim() !== "")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  return words.join(" ");
}

let refreshIntervalId = null;
let visibilityHandler = null;

/**
 * Starts periodic token refresh and listens for tab focus to refresh immediately.
 * Safe to call multiple times — cleans up previous listeners before re-registering.
 */
export function startTokenRefresh(keycloak) {
  // Always clean up first so re-login works and intervals don't stack
  stopTokenRefresh();

  if (!keycloak.tokenParsed) return;

  // Periodic refresh: every 30s, refresh if token expires within 60s
  refreshIntervalId = setInterval(() => {
    keycloak
      .updateToken(60)
      .then(() => {})
      .catch(() => {
        toast.error("Session expired. Please login again.");
        keycloak.logout({ redirectUri: window.location.origin });
      });
  }, 30000);

  // Refresh immediately when the browser tab regains focus.
  // Browsers throttle setInterval in background tabs, so the periodic
  // refresh may not run while the tab is hidden. This ensures the token
  // is fresh before React Query refetches on window focus.
  visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      keycloak
        .updateToken(30)
        .then(() => {})
        .catch(() => {
          toast.error("Session expired. Please login again.");
          keycloak.logout({ redirectUri: window.location.origin });
        });
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);
}

/**
 * Stops the periodic token refresh and removes the visibility listener.
 * Called on logout or before re-starting the refresh cycle.
 */
export function stopTokenRefresh() {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
}
